// CompanionTalkTab.tsx
import React, { useState } from "react";
import { MessageCircle } from "lucide-react";
import AstriaUKV2Shell from "../AstriaUKV2Shell";
import { useUKV2Colors } from "../AstriaUKV2ThemeContext";
import {
  AstriaUKV2ResultCard,
  UKV2LoadingState,
  UKV2ErrorState,
  UKV2ChatCta,
} from "../AstriaUKV2Card";
import { UKV2TextInput, UKV2SubmitButton } from "../AstriaUKV2Wizard";
import { useAstriaUKV2Tab } from "../useAstriaUKV2Tab";
import {
  AstriaUKV2CompanionTalkData,
  Message,
} from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Companion Talk";

// Helper function to extract JSON from the message content
const extractJSONFromMessage = (content: string): any => {
  try {
    // Look for the pattern <<<ASTRIA_UK_V2_DATA>>> ... <<<END_ASTRIA_UK_V2_DATA>>>
    const match = content.match(
      /<<<ASTRIA_UK_V2_DATA>>>\s*([\s\S]*?)\s*<<<END_ASTRIA_UK_V2_DATA>>>/,
    );
    if (match && match[1]) {
      return JSON.parse(match[1].trim());
    }
    return null;
  } catch (error) {
    console.error("Failed to parse JSON from message:", error);
    return null;
  }
};

// Colors must be read INSIDE AstriaUKV2Shell's subtree (see the note in
// ZodiacPersonalityTab.tsx) — these small components are rendered as
// `children`, not evaluated inline, so their hook calls resolve after the
// shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaUKV2CompanionTalkData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { colors } = useUKV2Colors();

  // If we have parsed data, show it as a single flowing chat reply — this
  // lane is a chat engine, not a structured reading, so no field labels or
  // section breaks here (matches the UK V2 companion_talk "no_reading_format"
  // / "no_paragraph_blocks" rule).
  if (data) {
    const reply = [data.reflection, data.observation, data.question]
      .filter((part) => part && part.trim())
      .join(" ");

    return (
      <>
        <AstriaUKV2ResultCard
          icon={<MessageCircle className="w-5 h-5" />}
          title="Companion Talk"
          shareText={reply}
        >
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.75,
              color: colors.bodyColor,
            }}
          >
            {reply}
          </p>
        </AstriaUKV2ResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          Start a New Check-in
        </button>
        <UKV2ChatCta onClick={goToChat} />
      </>
    );
  }

  // If we have a message but no parsed data, try to extract it
  if (message) {
    // Check if the message contains the ASTRIA marker
    if (message.content.includes("<<<ASTRIA_UK_V2_DATA>>>")) {
      const parsedData = extractJSONFromMessage(message.content);
      if (parsedData) {
        // If we successfully parsed the data, render the structured view
        return (
          <ResultView
            data={parsedData as AstriaUKV2CompanionTalkData}
            message={message}
            reset={reset}
            goToChat={goToChat}
          />
        );
      }
    }

    // If no structured data, show the raw message
    return (
      <AstriaUKV2ResultCard
        icon={<MessageCircle className="w-5 h-5" />}
        title="Companion Talk"
      >
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaUKV2ResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  feeling: string;
  setFeeling: (v: string) => void;
  onSubmit: () => void;
}> = ({ feeling, setFeeling, onSubmit }) => {
  const { colors } = useUKV2Colors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          Companion Talk
        </h2>
        <p
          style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}
        >
          It sounds like something's been on your mind. Tell me a little, if
          you'd like — no rush.
        </p>
      </div>

      <div style={{ marginBottom: 20 }}>
        <UKV2TextInput
          value={feeling}
          onChange={setFeeling}
          placeholder="What's been on your mind lately? (optional)"
        />
      </div>

      <UKV2SubmitButton onClick={onSubmit} label="Start the Check-in" />
    </>
  );
};

const CompanionTalkTab: React.FC = () => {
  const { loading, error, message, send, reset, goToChat } = useAstriaUKV2Tab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [feeling, setFeeling] = useState("");
  const started = !!message || loading || !!error;

  const handleSubmit = () =>
    send(
      `I'd like a Companion Talk check-in. Here's what's on my mind: ${feeling.trim() || "I'm not entirely sure, just wanted to talk it through."}`,
    );

  // Try to get structured data from the message
  const data = (() => {
    if (message?.astriaUKV2Data) {
      return message.astriaUKV2Data as AstriaUKV2CompanionTalkData | undefined;
    }
    // If no astriaUKV2Data, try to extract from content
    if (
      message?.content &&
      message.content.includes("<<<ASTRIA_UK_V2_DATA>>>")
    ) {
      const parsedData = extractJSONFromMessage(message.content);
      if (parsedData) {
        return parsedData as AstriaUKV2CompanionTalkData;
      }
    }
    return undefined;
  })();

  if (started) {
    return (
      <AstriaUKV2Shell active="companion-talk">
        {loading && <UKV2LoadingState label="Listening…" />}
        {!loading && error && (
          <UKV2ErrorState message={error} onRetry={handleSubmit} />
        )}
        {!loading && !error && (
          <ResultView
            data={data}
            message={message}
            reset={reset}
            goToChat={goToChat}
          />
        )}
      </AstriaUKV2Shell>
    );
  }

  return (
    <AstriaUKV2Shell active="companion-talk">
      <IntroForm
        feeling={feeling}
        setFeeling={setFeeling}
        onSubmit={handleSubmit}
      />
    </AstriaUKV2Shell>
  );
};

export default CompanionTalkTab;
